import { addDays, format, startOfDay } from 'date-fns';

export interface QuickDate {
  /** Wire format, identical to what `formatDateForUrl` produces. */
  value: string;
  /** First chip line: 'Hôm nay' | 'Ngày mai' | 'T4'. */
  label: string;
  /** Second chip line, always shown: '06/08'. */
  sub: string;
  date: Date;
  isWeekend: boolean;
}

/**
 * Indexed by `Date#getDay()`, so 0 is Sunday.
 *
 * date-fns' `vi` locale can't produce this form: its short weekday (`EEEEEE`) is
 * "Th 2" — the space reads badly in a narrow chip — and its narrow weekday
 * (`EEEEE`) collapses to "T" for five of the seven days. The compact "T2 / CN"
 * form is what Vietnamese calendars actually use, so it's spelled out here.
 */
export const WEEKDAY_CHIP = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'] as const;

/**
 * Builds the quick-date shortcut rail, starting at `from` and running forward.
 * Pure and deterministic — the caller owns "what is today", which keeps the
 * ambient clock out of render and makes this unit-testable.
 */
export function buildQuickDates(from: Date, count = 7): QuickDate[] {
  const base = startOfDay(from);

  return Array.from({ length: count }, (_, offset) => {
    const date = addDays(base, offset);
    const dayOfWeek = date.getDay();

    return {
      value: format(date, 'yyyy-MM-dd'),
      label: offset === 0 ? 'Hôm nay' : offset === 1 ? 'Ngày mai' : WEEKDAY_CHIP[dayOfWeek],
      sub: format(date, 'dd/MM'),
      date,
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
    };
  });
}
